import {
  ArrayMaxSize,
  ArrayMinSize,
  ArrayUnique,
  IsArray,
  IsIn,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
} from 'class-validator';
import type { FilePriority, FileStage, FileStatus } from './files.dto';

/**
 * The AI case assistant on one enforcement file.
 *
 * Two kinds of answer come back from it, and the contract keeps them apart:
 *
 * - **Facts** — the timeline, the open statutory clocks, the next statutory
 *   step and the escalation-risk indicator. Derived on the server from the
 *   record by fixed rules, never by a model, so they are the same every time
 *   and available even where a municipality has opted out of external AI.
 * - **AI text** — the narrative summary, a draft letter, a description of a
 *   photo. Always carries {@link AiProvenance}, which the portal turns into the
 *   "AI-generated — review before use" label. Nothing here is ever sent to
 *   anyone: a draft is text for the officer to edit and use themselves.
 */

/** Why the AI half of the assistant is not available for a file. */
export const ASSISTANT_UNAVAILABLE_REASONS = [
  /** The municipality has not turned the assistant on (the default). */
  'disabled',
  /** The file is anchored to no municipality, so there is no switch to read. */
  'no_municipality',
  /** The municipality is on the external-processing opt-out list. */
  'external_ai_opted_out',
  /** No model credentials are configured on this deployment. */
  'not_configured',
] as const;
export type AssistantUnavailableReason = (typeof ASSISTANT_UNAVAILABLE_REASONS)[number];

/** `GET /files/:id/assistant`. What the panel may offer on this file. */
export interface AssistantStatus {
  /** The assistant panel is shown at all — the municipality turned it on. */
  enabled: boolean;
  /** Model-backed features (narrative, drafts, photos) can be used. */
  aiAvailable: boolean;
  /** Why `aiAvailable` (or `enabled`) is false; null when both are true. */
  reason: AssistantUnavailableReason | null;
  /** The model a call would use, for the provenance line. */
  model: string | null;
}

/** Stamped on every piece of model output. The portal labels it from this. */
export interface AiProvenance {
  aiGenerated: true;
  model: string;
  generatedAt: string;
  /**
   * What was withheld from the model for this call — field names, never
   * values — so the officer knows what the text was written without.
   */
  redactedFields: string[];
}

export type AssistantLocale = 'en' | 'fr';
export const ASSISTANT_LOCALES: readonly AssistantLocale[] = ['en', 'fr'];

// ── Summary ──────────────────────────────────────────────────────────────────

/** One meaningful step from the file's trail. Labels come from `gov.activity.actions`. */
export interface AssistantTimelineEntry {
  at: string;
  action: string;
  /** Only the stage names of a transition; nothing personal is copied here. */
  from?: string | null;
  to?: string | null;
}

/** One open statutory clock, as `@munifocus/domain-deadlines` derives it. */
export interface AssistantDeadline {
  kind: string;
  statuteRef: string;
  deadlineIso: string;
  daysRemaining: number;
  expired: boolean;
}

/**
 * The next thing the law expects on this file. A code, so the portal says it in
 * the officer's language; `statuteRef` and `dueOn` where one applies.
 */
export const NEXT_STEP_CODES = [
  'complete_intake',
  'complete_review',
  'conduct_site_visit',
  'record_classification',
  'choose_escalation',
  'serve_notice',
  'await_appeal_window',
  'record_appeal_outcome',
  'await_kings_bench_window',
  'council_decision',
  'carry_out_work',
  'lay_information',
  'await_court_outcome',
  'await_compliance',
  'none',
] as const;
export type NextStepCode = (typeof NEXT_STEP_CODES)[number];

export interface NextStatutoryStep {
  code: NextStepCode;
  statuteRef: string | null;
  dueOn: string | null;
}

export interface CaseFacts {
  reference: string;
  stage: FileStage;
  status: FileStatus;
  priority: FilePriority;
  openedOn: string;
  daysOpen: number;
  /** Days since the file last changed stage; null when the trail does not say. */
  daysInStage: number | null;
}

export interface AiNarrative extends AiProvenance {
  locale: AssistantLocale;
  text: string;
}

/** `POST /files/:id/assistant/summary`. */
export interface CaseSummary {
  fileId: string;
  generatedAt: string;
  facts: CaseFacts;
  timeline: AssistantTimelineEntry[];
  deadlines: AssistantDeadline[];
  nextStep: NextStatutoryStep;
  /** Null when no model was asked — see `narrativeUnavailable`. */
  narrative: AiNarrative | null;
  narrativeUnavailable: AssistantUnavailableReason | 'failed' | 'not_requested' | null;
}

export class CaseSummaryDto {
  /** The narrative's language. The facts are codes and translate client-side. */
  @IsOptional()
  @IsIn(ASSISTANT_LOCALES)
  locale?: AssistantLocale;

  /** Facts only, no model call — what the mobile card asks for. */
  @IsOptional()
  @IsIn(['facts', 'full'])
  mode?: 'facts' | 'full';
}

// ── Draft correspondence ─────────────────────────────────────────────────────

export const DRAFT_KINDS = ['owner_letter', 'complainant_reply'] as const;
export type DraftKind = (typeof DRAFT_KINDS)[number];

export const DRAFT_INSTRUCTIONS_MAX = 1000;

export class DraftCorrespondenceDto {
  @IsIn(DRAFT_KINDS)
  kind!: DraftKind;

  @IsIn(ASSISTANT_LOCALES)
  locale!: AssistantLocale;

  /** What the officer wants the letter to say, in their own words. */
  @IsOptional()
  @IsString()
  @MaxLength(DRAFT_INSTRUCTIONS_MAX)
  instructions?: string;
}

/** `POST /files/:id/assistant/draft`. Text for the officer; never sent by the system. */
export interface CorrespondenceDraft extends AiProvenance {
  kind: DraftKind;
  locale: AssistantLocale;
  subject: string;
  body: string;
  /**
   * Placeholders left in the text because the server could not fill them —
   * e.g. `[COMPLAINANT_NAME]` for an anonymous complainant. The officer
   * replaces them before using the letter.
   */
  placeholders: string[];
}

// ── Photo condition assessment ───────────────────────────────────────────────

/**
 * Visible conditions a photo can suggest, in the vocabulary of a Dangerous &
 * Unsightly classification. Suggestions only: the officer confirms each one.
 */
export const PHOTO_CONDITION_CODES = [
  'accumulated_refuse',
  'derelict_vehicle',
  'overgrown_vegetation',
  'structural_damage',
  'broken_openings',
  'fire_damage',
  'open_to_entry',
  'hazardous_materials',
  'pest_harbourage',
  'unsafe_accessory_structure',
  'none_visible',
] as const;
export type PhotoConditionCode = (typeof PHOTO_CONDITION_CODES)[number];

/** Most photos one assessment reads — each is a full image sent to the model. */
export const MAX_ASSESSED_PHOTOS = 4;

export class AssessPhotosDto {
  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(MAX_ASSESSED_PHOTOS)
  @ArrayUnique()
  @IsUUID('all', { each: true })
  evidenceIds!: string[];

  @IsIn(ASSISTANT_LOCALES)
  locale!: AssistantLocale;
}

export interface PhotoAssessment {
  evidenceId: string;
  /** Plain description of what is visible — never a finding. */
  observations: string[];
  conditions: PhotoConditionCode[];
  /** Why this photo was not read; the others in the request still were. */
  skipped: 'not_image' | 'too_large' | 'unreadable' | null;
}

/** `POST /files/:id/assistant/photo-assessment`. */
export interface PhotoAssessmentResult extends AiProvenance {
  locale: AssistantLocale;
  photos: PhotoAssessment[];
}

/**
 * `POST /files/:id/assistant/photo-assessment/confirm`. The officer's decision
 * on a suggestion, which is what goes on the file's trail — the model's own
 * words never do.
 */
export class ConfirmPhotoAssessmentDto {
  @IsUUID()
  evidenceId!: string;

  @IsArray()
  @ArrayMaxSize(PHOTO_CONDITION_CODES.length)
  @ArrayUnique()
  @IsIn(PHOTO_CONDITION_CODES, { each: true })
  conditions!: PhotoConditionCode[];
}

// ── Escalation risk ──────────────────────────────────────────────────────────

export type RiskLevel = 'low' | 'moderate' | 'high';

export const RISK_REASON_CODES = [
  'deadline_overdue',
  'deadline_imminent',
  'emergency',
  'urgent_priority',
  'elevated_priority',
  'repeat_property',
  'multiple_complaints',
  'failed_service',
  'appeal_filed',
  'warrant_in_play',
  'proceedings_filed',
  'stalled_in_stage',
] as const;
export type RiskReasonCode = (typeof RISK_REASON_CODES)[number];

export interface RiskReason {
  code: RiskReasonCode;
  /** Points this reason added to the score. */
  weight: number;
  /** Interpolation values for the reason's sentence (a count, a number of days). */
  params?: Record<string, number>;
}

/**
 * `GET /files/:id/assistant/escalation-risk`. A rule-based indicator, not a
 * model's opinion: every point comes with the reason that added it, so the
 * officer can see — and disagree with — exactly why.
 */
export interface EscalationRisk {
  fileId: string;
  level: RiskLevel;
  /** 0–100. */
  score: number;
  reasons: RiskReason[];
  computedAt: string;
  aiGenerated: false;
}
